import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, Lock } from 'lucide-react';

// View-only, watermarked preview gallery for the Home Health & Hospice Travel Logbook.
// Lower-resolution watermarked previews only — original files/URLs are never exposed.
// Downloading, printing, right-click saving and dragging are disabled (reasonable deterrents;
// screenshots cannot be fully prevented).

const PAGES = [
  { src: '/book-previews/hhtl-a7f2.jpg', label: 'Travel Logbook Cover' },
  { src: '/book-previews/hhtl-b3d9.jpg', label: 'Respite Stays Page' },
  { src: '/book-previews/hhtl-c1e8.jpg', label: 'Expense Record Page' },
  { src: '/book-previews/hhtl-d6b4.jpg', label: 'Non-Visit Activity Page' },
  { src: '/book-previews/hhtl-e9c7.jpg', label: 'Daily Log Sample Page' },
  { src: '/book-previews/hhtl-f2a5.jpg', label: 'Contacts Sample Page' },
];

const noDrag = (e) => { e.preventDefault(); return false; };

const ProtectedImage = ({ src, alt, className, imgClassName }) => (
  <div
    className={`relative overflow-hidden select-none ${className || ''}`}
    onContextMenu={noDrag}
    onDragStart={noDrag}
    style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
  >
    <img
      src={src}
      alt={alt}
      draggable="false"
      onContextMenu={noDrag}
      onDragStart={noDrag}
      className={`block w-full h-full pointer-events-none ${imgClassName || ''}`}
      style={{ WebkitUserDrag: 'none', WebkitTouchCallout: 'none' }}
    />
    {/* transparent shield intercepts right-click / long-press / drag on the pixels */}
    <div className="absolute inset-0" onContextMenu={noDrag} onDragStart={noDrag} aria-hidden="true" />
  </div>
);

const ProtectedBookGallery = () => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const show = (i) => { setIdx(i); setOpen(true); };
  const close = useCallback(() => setOpen(false), []);
  const prev = useCallback(() => setIdx((i) => (i - 1 + PAGES.length) % PAGES.length), []);
  const next = useCallback(() => setIdx((i) => (i + 1) % PAGES.length), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      // deter save/print shortcuts while viewing
      else if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'c'].includes(e.key.toLowerCase())) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close, prev, next]);

  return (
    <div
      className="w-full no-print"
      data-testid="book-gallery"
      onContextMenu={noDrag}
    >
      {/* Lead cover */}
      <button
        type="button"
        onClick={() => show(0)}
        className="group relative block w-full max-w-[220px] mx-auto rounded-lg shadow-lg overflow-hidden cursor-zoom-in"
        data-testid="book-cover-thumb"
        aria-label="View Travel Logbook cover"
      >
        <ProtectedImage src={PAGES[0].src} alt="Home Health & Hospice Travel Logbook cover" imgClassName="object-cover" className="aspect-[3/4]" />
        <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors flex items-center justify-center">
          <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 drop-shadow transition-opacity" />
        </span>
      </button>

      {/* Sample page thumbnails (new pages + existing samples) */}
      <p className="text-center text-xs font-medium text-gray-500 mt-4 mb-2">Look inside — sample pages</p>
      <div className="grid grid-cols-5 gap-2">
        {PAGES.slice(1).map((p, i) => (
          <button
            key={p.src}
            type="button"
            onClick={() => show(i + 1)}
            className="group relative rounded-md shadow overflow-hidden cursor-zoom-in"
            data-testid={`book-page-thumb-${i}`}
            aria-label={`View ${p.label}`}
          >
            <ProtectedImage src={p.src} alt={p.label} imgClassName="object-cover object-top" className="aspect-[3/4]" />
            <span className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors" />
          </button>
        ))}
      </div>
      <p className="flex items-center justify-center gap-1 text-[11px] text-gray-400 mt-3">
        <Lock className="w-3 h-3" /> Preview only · watermarked · © INASW
      </p>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 flex items-center justify-center p-4"
          data-testid="book-lightbox"
          onClick={close}
          onContextMenu={noDrag}
        >
          <button
            onClick={close}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            data-testid="book-lightbox-close"
            aria-label="Close preview"
          >
            <X className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 sm:left-6 text-white/70 hover:text-white p-2"
            data-testid="book-lightbox-prev"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-9 h-9" />
          </button>
          <div
            className="max-h-[88vh] max-w-[90vw] sm:max-w-[520px]"
            onClick={(e) => e.stopPropagation()}
          >
            <ProtectedImage
              src={PAGES[idx].src}
              alt={PAGES[idx].label}
              className="rounded-lg shadow-2xl max-h-[82vh]"
              imgClassName="object-contain max-h-[82vh]"
            />
            <p className="text-center text-white/80 text-sm mt-3">{PAGES[idx].label} · {idx + 1} / {PAGES.length}</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 sm:right-6 text-white/70 hover:text-white p-2"
            data-testid="book-lightbox-next"
            aria-label="Next page"
          >
            <ChevronRight className="w-9 h-9" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProtectedBookGallery;
